'use client';

import { useState } from 'react';
import { Box, Typography, Stack, TextField, Button, Tooltip } from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface ProfileInfoProps {
  name: string;
  bio?: string | null;
  isOwnProfile: boolean;
  onSave: (name: string, bio: string) => Promise<void>;
}

export function ProfileInfo({
  name: initialName,
  bio: initialBio,
  isOwnProfile,
  onSave,
}: ProfileInfoProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName);
  const [bio, setBio] = useState(initialBio || '');

  const handleSave = async () => {
    try {
      await onSave(name, bio);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setName(initialName);
    setBio(initialBio || '');
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      {isEditing ? (
        <Stack spacing={2} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Bio"
            value={bio}
            onChange={e => setBio(e.target.value)}
            placeholder="Tell others about yourself..."
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!name.trim()}
            >
              Save Changes
            </Button>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              {name}
            </Typography>
            <Typography color="text.secondary">
              {bio ||
                (isOwnProfile ? 'Add a bio to tell others about yourself' : 'No bio provided')}
            </Typography>
          </Box>
          {isOwnProfile && (
            <Box sx={{ ml: 2 }}>
              <Tooltip title="Edit Profile">
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  size="small"
                >
                  Edit
                </Button>
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
